import React, { useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Switch,
  Typography,
} from '@mui/material'
import { advancedGet, advancedPut } from '../api/client'
import { AdvancedRoleChatSetting } from '../api/types'
import { useAdvancedResource } from '../api/useAdvancedResource'
import DialogHeader from '../components/DialogHeader'
import FailureMessage from '../components/FailureMessage'
import SectionCard from '../components/SectionCard'
import { roleLabelFor } from '../dashboard/careTeam/roleCategories'
import { advancedColors } from '../theme'
import { adminFailureReason, isRejected } from './adminFailureReason'

type Change = { role: string; chatEnabled: boolean }

// The service has no setting for the therapist (ADR-158).
const SHARED_CHAT_ROLE = 'therapist'
const DISABLED_CHAT_ROLE = 'care-coordinator'

const ConfirmChatChangeDialog = ({
  change,
  saving,
  onCancel,
  onConfirm,
}: {
  change: Change | null
  saving: boolean
  onCancel: () => void
  onConfirm: () => void
}) => {
  const label = change ? roleLabelFor(change.role) : ''
  // A save in flight still lands, and its own close would dismiss the next dialog.
  const dismiss = () => {
    if (!saving) {
      onCancel()
    }
  }

  return (
    <Dialog open={change !== null} onClose={dismiss} maxWidth="xs" fullWidth>
      <DialogHeader title={`Turn ${change?.chatEnabled ? 'on' : 'off'} chat for ${label}?`} onClose={dismiss} />
      <DialogContent>
        <Typography variant="body2" sx={{ color: advancedColors.inkMuted, mb: 2 }}>
          {change?.chatEnabled
            ? `Patients with a ${label} on their care team will see them in the app’s chat list and can message them.`
            : `Patients will no longer see ${label} in the app’s chat list. Existing messages are kept, and providers can still read them.`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          color={change?.chatEnabled ? 'primary' : 'error'}
          variant="contained"
          onClick={onConfirm}
          disabled={saving}
        >
          {change?.chatEnabled ? 'Turn on' : 'Turn off'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const AdvancedAdminRoleChats = () => {
  const resource = useAdvancedResource(
    (signal) => advancedGet<{ roles: AdvancedRoleChatSetting[] }>('/admin/role-chat-settings', signal),
    []
  )
  const [saved, setSaved] = useState<AdvancedRoleChatSetting[]>()
  const [pending, setPending] = useState<Change | null>(null)
  const [saving, setSaving] = useState(false)
  const { addToast } = useToasts()

  const roles = saved ?? (resource.status === 'ready' ? resource.data.roles : undefined)

  const cancel = () => setPending(null)

  const reload = async (): Promise<string> => {
    try {
      const current = await advancedGet<{ roles: AdvancedRoleChatSetting[] }>('/admin/role-chat-settings')
      setSaved(current.roles)
      return 'The switches show the current settings.'
    } catch {
      return 'Reload the page to check the current settings.'
    }
  }

  const confirm = async () => {
    if (!pending) {
      return
    }

    setSaving(true)

    try {
      const response = await advancedPut<{ roles: AdvancedRoleChatSetting[] }>(
        `/admin/role-chat-settings/${encodeURIComponent(pending.role)}`,
        { chatEnabled: pending.chatEnabled }
      )
      setSaved(response.roles)
      setPending(null)
    } catch (error) {
      setPending(null)
      const failure = isRejected(error)
        ? { summary: 'Failed to save that change.', reason: `${adminFailureReason(error)} Nothing was changed.` }
        : { summary: 'Failed to confirm that change.', reason: `${adminFailureReason(error)} ${await reload()}` }
      addToast(<FailureMessage failure={failure} />, { appearance: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <SectionCard
      title="Patient chat configuration"
      help="Choose which care team roles patients can message in the app."
      collapsible
    >
      {resource.status === 'loading' && !roles && <LinearProgress />}
      {resource.status === 'error' && !roles && (
        <Alert severity="error" action={<Button onClick={resource.retry}>Retry</Button>}>
          <FailureMessage
            failure={{ summary: 'Failed to load the role chat settings.', reason: adminFailureReason(resource.error) }}
          />
        </Alert>
      )}
      {roles?.length === 0 && <Typography variant="body2">No roles to set up yet.</Typography>}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {roles && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body1">{roleLabelFor(SHARED_CHAT_ROLE)}</Typography>
            <Switch checked disabled inputProps={{ 'aria-label': `Chat with ${roleLabelFor(SHARED_CHAT_ROLE)}` }} />
          </Box>
        )}
        {roles && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body1">{roleLabelFor(DISABLED_CHAT_ROLE)}</Typography>
            <Switch
              checked={false}
              disabled
              inputProps={{ 'aria-label': `Chat with ${roleLabelFor(DISABLED_CHAT_ROLE)}` }}
            />
          </Box>
        )}
        {roles
          ?.filter(({ role }) => role !== DISABLED_CHAT_ROLE && role !== 'care-navigator')
          .map(({ role, chatEnabled }) => (
            <Box key={role} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body1">{roleLabelFor(role)}</Typography>
              <Switch
                checked={chatEnabled}
                onChange={(_event, checked) => setPending({ role, chatEnabled: checked })}
                inputProps={{ 'aria-label': `Chat with ${roleLabelFor(role)}` }}
              />
            </Box>
          ))}
      </Box>
      <ConfirmChatChangeDialog change={pending} saving={saving} onCancel={cancel} onConfirm={confirm} />
    </SectionCard>
  )
}

export default AdvancedAdminRoleChats
